<template>
  <b-container>
    <div class="mt-4 d-flex justify-content-between align-items-center">
      <back-arrow :bilan-type="bilanType"
                  :params="{operationId: operationId}"
                  :sub-title-ope="operationName"
                  :title="$t('Devis') + ' & ' + $t('Factures')"
                  route-back="OperationShow"/>

      <div class="d-flex justify-content-end ml-auto mt-0">
        <b-button class="ml-2 fixed-card-size-button" variant="primary"
                  @click="clickOnCreateDevisBtn()">
          <plus-icon class="mr-1" size="18"/>
          {{ $t('Add') }} {{ $t('a') }} {{ $t('devis') }}
        </b-button>
        <b-button class="ml-2 fixed-card-size-button" variant="primary"
                  @click="clickOnCreateFactureBtn()">
          <plus-icon class="mr-1" size="18"/>
          {{ $t('Add') }} {{ $t('a.e') }} {{ $t('facture') }}
        </b-button>
      </div>
    </div>

    <b-card class="mt-2 mb-4 shadow-sm">
      <!-- Filtres -->
      <div class="row ">
        <div class="col-md-2">
          <b-form-group>
            <b-form-select
              id="type-filter"
              v-model="selectedTypeFilter"
              :options="typeFilterOptions"
              @change="applyFilters">
            </b-form-select>
          </b-form-group>
        </div>
        <div class="col-md-2">
          <b-form-group>
            <b-form-select
              id="document-filter"
              v-model="selectedDocumentFilter"
              :options="documentFilterOptions"
              @change="applyFilters">
            </b-form-select>
          </b-form-group>
        </div>
        <div class="col-md-2">
          <b-form-group>
            <b-form-select
              id="artisan-filter"
              v-model="selectedArtisanFilter"
              :options="artisanFilterOptions"
              @change="applyFilters">
            </b-form-select>
          </b-form-group>
        </div>
        <div class="col-md-2">
          <b-form-group>
            <b-form-select
              id="status-filter"
              v-model="selectedStatusFilter"
              :options="statusFilterOptions"
              @change="applyFilters">
            </b-form-select>
          </b-form-group>
        </div>
        <div class="col-md-2">
          <b-form-group>
            <b-form-select
              id="lot-filter"
              v-model="selectedLotFilter"
              :options="lotFilterOptions"
              @change="applyFilters">
            </b-form-select>
          </b-form-group>
        </div>
      </div>

      <b122-table
        :key="tableKey"
        ref="B122TableDevisFactures"
        :create-item="false"
        :delete-item="true"
        :edit-item="true"
        :empty-text="$t('devis.factures.emptyText').toString()"
        :empty-title="$t('devis.factures.emptyTitle').toString()"
        :fields="computedTableFields"
        :get-b122-table-results-url="getB122TableResultsUrl"
        :identifier="'B122TableDevisFactures'"
        :use-filters="true"
        :use-pagination="true"
        init-b122-table-url="/operationDevisFacture/initB122Table"
        @clickOnDeleteBtn="clickOnDeleteBtn"
        @clickOnEditBtn="clickOnEditBtn"
        @clickOnShowBtn="clickOnShowBtn"
        @tableRefreshed="onTableRefreshed"
        @resetDisplay="onResetDisplay">

        <!-- Templates comme OperationDevisIndex -->
        <template #cell(operationDevisFacture.nom)="data">
          <div>
            <div class="font-weight-bold">{{ data.value }}</div>
            <small v-if="data.item.operationDevisFacture.artisan"
                   class="text-muted-grey">{{ data.item.operationDevisFacture.artisan }}</small>
          </div>
        </template>

        <template #cell(operationDevisFacture.devis)="data">
          <div v-if="data.item.operationDevisFacture.devis">
            <!-- Affichage d'un seul devis par ligne -->
            <div class="d-flex align-items-center">
              <span class="font-weight-medium">N° {{ data.item.operationDevisFacture.devis.numero || 'N/A' }}</span>
              <span v-if="data.item.operationDevisFacture.devis.status === 'VALIDATED'"
                    class="ml-2 text-success">✓</span>
              <span v-else-if="data.item.operationDevisFacture.devis.status === 'REFUSED'"
                    class="ml-2 text-danger">✗</span>
            </div>
            <div v-for="document in data.item.operationDevisFacture.devis.documents" :key="document.id" class="mt-1">
              <b-link class="light-blue-color underline-text" style="font-size: 0.85rem;"
                      @click="getDocument(document.fileUrl)">
                {{ truncateText(document.fileName, 25) }}
              </b-link>
            </div>
          </div>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell(operationDevisFacture.montantTotal)="data">
          <span class="font-weight-bold">{{ getBudgetFromDevis(data.item.operationDevisFacture) }}</span>
        </template>

        <template #cell(operationDevisFacture.factures)="data">
          <div class="d-flex flex-column align-items-start justify-content-center h-100">
            <div v-if="data.item.operationDevisFacture.facture">
              <div>
                <div class="font-weight-medium">
                  {{ data.item.operationDevisFacture.facture.libelle || 'N/A' }}
                </div>
                <div v-for="document in data.item.operationDevisFacture.facture.documents" :key="document.id"
                     class="mt-1">
                  <b-link class="light-blue-color underline-text" style="font-size: 0.85rem;"
                          @click="getDocument(document.fileUrl)">
                    {{ truncateText(document.fileName, 25) }}
                  </b-link>
                </div>
              </div>
            </div>
            <span v-else class="text-muted">-</span>
          </div>
        </template>

        <template #cell(operationDevisFacture.status)="data">
          <div v-if="data.item.operationDevisFacture.facture">
            <span :class="getStatusClass(data.item.operationDevisFacture.facture.status)" class="status-badge">
              {{
                getStatusText(data.item.operationDevisFacture.facture.status, [data.item.operationDevisFacture.facture])
              }} - {{ convertCentimesToEuros(data.item.operationDevisFacture.facture.montant) }}
            </span>
          </div>
          <div
            v-else-if="data.item.operationDevisFacture.devis && data.item.operationDevisFacture.devis.status === 'VALIDATED'">
            <span class="status-a-payer status-badge">
              {{ $t('status.aPayer') }} - {{ convertCentimesToEuros(data.item.operationDevisFacture.devis.montant) }}
            </span>
          </div>
          <span v-else class="text-muted">-</span>
        </template>

      </b122-table>
    </b-card>


    <b-modal
      id="edit-choice-modal"
      :title="$t('Modify.poste')"
      centered
      hide-footer
      @hidden="hideEditModal">

      <div>
        <p class="mb-4">{{ $t('Que souhaitez-vous modifier ?') }}</p>

        <div class="row">
          <div class="col-md-6">
            <b-button
              block
              class="mb-3"
              size="lg"
              variant="primary"
              @click="editDevis">
              <i class="fas fa-file-invoice mr-2"></i>
              {{ $t('Modifier') }} {{ $t('devis') }}
            </b-button>
          </div>
          <div class="col-md-6">
            <b-button
              block
              class="mb-3"
              size="lg"
              variant="primary"
              @click="editFactures">
              <i class="fas fa-receipt mr-2"></i>
              {{ $t('Modifier') }} {{ $t('facture') }}
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Modale pour choisir ce qu'on veut voir -->
    <b-modal
      id="show-choice-modal"
      :title="$t('Consulter')"
      centered
      hide-footer
      @hidden="hideShowModal">

      <div>
        <p class="mb-4">{{ $t('Que souhaitez-vous consulter ?') }}</p>

        <div class="row">
          <div class="col-md-6">
            <b-button
              block
              class="mb-3"
              size="lg"
              variant="primary"
              @click="showDevis">
              <i class="fas fa-file-invoice mr-2"></i>
              {{ $t('Consulter') }} {{ $t('devis') }}
            </b-button>
          </div>
          <div class="col-md-6">
            <b-button
              block
              class="mb-3"
              size="lg"
              variant="primary"
              @click="showFacture">
              <i class="fas fa-receipt mr-2"></i>
              {{ $t('Consulter') }} {{ $t('facture') }}
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>


    <!-- Modale pour sélectionner un devis à modifier -->
    <b-modal
      id="devis-selection-modal"
      :title="$t('Sélectionner un devis à modifier')"
      centered
      hide-footer
      @hidden="hideDevisModal">

      <div v-if="selectedItemForEdit && devisArray.length > 0">
        <p class="mb-4">{{ $t('Choisissez le devis à modifier :') }}</p>

        <div class="devis-list">
          <div
            v-for="devis in devisArray"
            :key="devis.id"
            class="devis-item mb-3 p-3 border rounded cursor-pointer"
            @click="editDevisItem(devis)">

            <div class="d-flex justify-content-between align-items-center">
              <div>
                <strong>{{ devis.numero || 'N/A' }}</strong>
                <div class="text-muted">
                  {{ convertCentimesToEuros(devis.montant) }}
                  <span v-if="devis.tauxTVA"> - TVA {{ devis.tauxTVA }}%</span>
                </div>
              </div>
              <div>
                <span v-if="devis.status === 'VALIDATED'">
                  <i class="fas fa-check-circle"></i>{{ $t('Validé') }}
                </span>
                <span v-else>
                  <i class="fas fa-clock"></i>{{ $t('En attente') }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="text-center">
        <p class="text-muted">{{ $t('Aucun devis associé à ce poste') }}</p>
      </div>
    </b-modal>

    <!-- Modale de sélection du type de devis -->
    <b-modal
      id="devis-type-selection-modal"
      :title="$t('Ajouter un devis')"
      centered
      hide-footer
      @hidden="hideDevisTypeModal">

      <div>
        <p class="mb-4">{{ $t('Que souhaitez-vous créer ?') }}</p>

        <div class="row">
          <div class="col-md-6">
            <b-button
              block
              class="mb-3"
              size="lg"
              variant="primary"
              @click="createConstructionDevis">
              <i class="fas fa-hammer mr-2"></i>
              {{ $t('Travaux') }}
            </b-button>
          </div>
          <div class="col-md-6">
            <b-button
              block
              class="mb-3"
              size="lg"
              variant="primary"
              @click="createHonoraireDevis">
              <i class="fas fa-gavel mr-2"></i>
              {{ $t('Honoraires') }}
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Modale de confirmation de suppression -->
    <b-modal
      id="delete-confirmation-modal"
      centered
      hide-footer
      title="Confirmation de suppression"
      @hidden="cancelDelete">

      <div v-if="selectedItemForDelete">


        <!-- Si on a les deux éléments (devis et facture) et pas de filtre spécifique -->
        <div v-if="hasDevisAndFactureForDelete && !isFilteredView">
          <p class="mb-4">{{ $t('Que souhaitez-vous supprimer ?') }}</p>

          <div class="row">
            <div class="col-md-6">
              <b-button
                block
                class="mb-3"
                size="lg"
                variant="primary"
                @click="deleteFactureOnly">
                <i class="fas fa-receipt mr-2"></i>
                {{ $t('Supprimer') }} {{ $t('facture') }}
              </b-button>
              <small class="text-muted d-block text-center">Le devis sera conservé</small>
            </div>
            <div class="col-md-6">
              <b-button
                block
                class="mb-3"
                size="lg"
                variant="secondary"
                @click="deleteCascade">
                <i class="fas fa-trash-alt mr-2"></i>
                {{ $t('Tout supprimer') }}
              </b-button>
              <small class="text-muted d-block text-center">Devis et facture</small>
            </div>
          </div>
        </div>

        <!-- Cas normal : suppression simple avec boutons OK/Annuler -->
        <div v-else>
          <div v-if="!isFilteredView" class="alert alert-warning">
            <i class="fas fa-exclamation-triangle mr-2"></i>
            <strong>{{ $t('Attention') }}</strong> {{ $t('action.delete.cascade') }} :
            <ul class="mb-0 mt-2">
              <li>{{ $t('all.factures.associated') }}</li>
              <li>{{ $t('all.devis.associated') }}</li>
              <li>{{ $t('poste/honoraire.conserved') }}</li>
            </ul>
          </div>

          <div class="mt-4 text-right">
            <b-button variant="primary" class="mr-3" @click="cancelDelete">
              {{ $t('Annuler') }}
            </b-button>
            <b-button variant="secondary" @click="confirmDelete">
              {{ $t('Supprimer') }}
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>
  </b-container>
</template>

<script>
import api from "../../services/api"
import BackArrow from "../../components/b122/BackArrow"
import B122Table from "../../components/b122/B122Table"

export default {
  name: "OperationDevisFacturesIndex",

  components: {
    BackArrow,
    B122Table
  },

  data() {
    return {
      operationId: this.$route.params.operationId,
      operationName: "",
      isHT: false,
      bilanType: "",
      totalCount: 0,
      selectedTypeFilter: null,
      selectedDocumentFilter: null,
      selectedArtisanFilter: null,
      selectedStatusFilter: null,
      selectedLotFilter: null,
      typeFilterOptions: [
        {value: null, text: this.$t('Travaux') + ' / ' + this.$t('Honoraires')},
        {value: 'construction', text: this.$t('Travaux')},
        {value: 'honoraire', text: this.$t('Honoraires')}
      ],
      documentFilterOptions: [
        {value: null, text: this.$t('Devis') + ' / ' + this.$t('Factures')},
        {value: 'devis_all', text: this.$t('Devis')},
        {value: 'factures_all', text: this.$t('Factures')},
        {value: 'factures_only', text: this.$t('Factures') + ' ' + this.$t('sans devis')}
      ],
      artisanFilterOptions: [
        {value: null, text: this.$t('Artisans')}
      ],
      lotFilterOptions: [
        {value: null, text: this.$t('Lots')}
      ],
      statusFilterOptions: [
        {value: null, text: this.$t('Statut')},
        {value: 'PAID', text: this.$t('status.paye')},
        {value: 'UNPAID', text: this.$t('status.aPayer')},
        {value: 'PENDING', text: this.$t('status.enAttente')}
      ],
      tableKey: 0,
      selectedItemForEdit: null,
      selectedItemForDelete: null,
      operationBilanId: null,
      simulationId: null,
    }
  },

  computed: {
    computedTableFields() {
      if (this.selectedDocumentFilter === 'devis_all') {
        return [
          { label: this.$t('See'), key: 'show', sortable: false },
          { label: this.$t('Devis'), key: 'operationDevisFacture.devis', sortable: true },
          { label: this.$t('Nom'), key: 'operationDevisFacture.nom', sortable: true },
          { label: this.$t(`Devis.montant.${this.isHT ? 'ht' : 'ttc'}`), key: 'operationDevisFacture.montantTotal', sortable: true },
          { label: '', key: 'actions', sortable: false },
        ]
      }
      return [
        { label: this.$t('See'), key: 'show', sortable: false },
        { label: this.$t('Nom'), key: 'operationDevisFacture.nom', sortable: true },
        { label: this.$t('Devis'), key: 'operationDevisFacture.devis', sortable: true },
        { label: this.$t(`Devis.montant.${this.isHT ? 'ht' : 'ttc'}`), key: 'operationDevisFacture.montantTotal', sortable: true },
        { label: this.$t('Facture'), key: 'operationDevisFacture.factures', sortable: false },
        { label: this.$t('Statut'), key: 'operationDevisFacture.status', sortable: false },
        { label: '', key: 'actions', sortable: false },
      ]
    },

    getB122TableResultsUrl() {
      let url = `/operationDevisFacture/getB122TableResults?restrictbylong-operationId=${this.operationId}`
      if (this.selectedTypeFilter) {
        url += `&typeFilter=${this.selectedTypeFilter}`
      }
      if (this.selectedDocumentFilter) {
        url += `&documentFilter=${this.selectedDocumentFilter}`
      }
      if (this.selectedArtisanFilter) {
        url += `&artisanFilter=${this.selectedArtisanFilter}`
      }
      if (this.selectedStatusFilter) {
        url += `&statusFilter=${this.selectedStatusFilter}`
      }
      if (this.selectedLotFilter) {
        url += `&lotFilter=${this.selectedLotFilter}`
      }

      return url
    },

    facturesArray() {

      if (!this.selectedItemForEdit) {
        return []
      }

      if (this.selectedItemForEdit.facture) {
        return [this.selectedItemForEdit.facture]
      }

      if (this.selectedItemForEdit.factures && Array.isArray(this.selectedItemForEdit.factures)) {
        return this.selectedItemForEdit.factures
      }

      return []
    },

    devisArray() {
      if (!this.selectedItemForEdit || !this.selectedItemForEdit.devis) {
        return []
      }

      if (this.selectedItemForEdit.devis.id) {
        return [this.selectedItemForEdit.devis]
      }

      return []
    },

    hasDevisAndFactureForDelete() {
      if (!this.selectedItemForDelete) return false
      const hasDevis = this.selectedItemForDelete.devis && this.selectedItemForDelete.devis.id
      const hasFacture = this.selectedItemForDelete.facture && this.selectedItemForDelete.facture.id
      return hasDevis && hasFacture
    },

    isFilteredView() {
      return this.selectedDocumentFilter === 'factures_all' || this.selectedDocumentFilter === 'factures_only'
    }
  },

  async mounted() {

    await this.fetchData()
    this.loadFiltersFromStorage()
    await this.loadArtisans()
    await this.loadLots()

    setTimeout(() => {

    }, 3000)
  },
  beforeDestroy() {
    this.saveFiltersToStorage()
  },

  methods: {
    onResetDisplay() {
      this.selectedTypeFilter = null
      this.selectedDocumentFilter = null
      this.selectedArtisanFilter = null
      this.selectedStatusFilter = null
      this.selectedLotFilter = null
      this.applyFilters()
    },
    getFiltersStorageKey() {
      return `OperationDevisFacturesIndex:filters:${this.operationId}`
    },

    loadFiltersFromStorage() {
      try {
        const saved = sessionStorage.getItem(this.getFiltersStorageKey())
        if (saved) {
          const obj = JSON.parse(saved)
          this.selectedTypeFilter = obj.selectedTypeFilter || null
          this.selectedDocumentFilter = obj.selectedDocumentFilter || null
          this.selectedArtisanFilter = obj.selectedArtisanFilter || null
          this.selectedStatusFilter = obj.selectedStatusFilter || null
          this.selectedLotFilter = obj.selectedLotFilter || null
          this.applyFilters()
        }
      } catch (e) {}
    },

    saveFiltersToStorage() {
      try {
        const toSave = JSON.stringify({
          selectedTypeFilter: this.selectedTypeFilter,
          selectedDocumentFilter: this.selectedDocumentFilter,
          selectedArtisanFilter: this.selectedArtisanFilter,
          selectedStatusFilter: this.selectedStatusFilter,
          selectedLotFilter: this.selectedLotFilter,
        })
        sessionStorage.setItem(this.getFiltersStorageKey(), toSave)
      } catch (e) {}
    },
    async fetchData() {
      await this.getOperation()
      await this.fetchOperationBilanId()
    },

    async getOperation() {
      try {
        const response = await api.get(`/operation/${this.operationId}`)
        this.operationName = response.data.name || response.data.toString
        this.bilanType = response.data.bilanType
        this.isHT = response.data.isHT
        this.simulationId = response.data.simulation && response.data.simulation.id
      } catch (error) {
        console.error('Erreur lors de la récupération de l\'opération:', error)
      }
    },

    async fetchOperationBilanId() {
      try {
        const response = await api.get(`/operation/getOperationBilanId/${this.operationId}`)
        this.operationBilanId = response.data.id
      } catch (error) {
        console.error('Erreur lors de la récupération de l\'operationBilanId:', error)
      }
    },

    getOperationBilanId() {
      return this.operationBilanId
    },

    clickOnShowBtn(row) {
      const item = row.item.operationDevisFacture
      this.selectedItemForEdit = item

      if (this.selectedDocumentFilter === 'devis_all' && item && item.devis && item.devis.id) {
        this.showDevis()
        return
      }

      const hasDevis = item && item.devis && item.devis.id
      const hasFacture = item && item.facture && item.facture.id


      if (hasDevis && hasFacture) {
        this.$bvModal.show('show-choice-modal')
      } else if (hasFacture) {
        this.showFacture()
      } else if (hasDevis) {
        this.showDevis()
      } else {
        this.$bvToast.toast(this.$t('Aucune destination disponible pour cette ligne'), {
          variant: 'warning',
          solid: true
        })
      }
    },

    clickOnEditBtn(row) {
      this.selectedItemForEdit = row.item.operationDevisFacture

      const hasDevis = this.selectedItemForEdit.devis && this.selectedItemForEdit.devis.id
      const hasFacture = this.selectedItemForEdit.facture && this.selectedItemForEdit.facture.id

      if (this.selectedDocumentFilter === 'devis_all' && hasDevis) {
        this.$router.push({
          name: 'OperationDevisEdit',
          params: {
            operationDevisId: this.selectedItemForEdit.devis.id,
            operationId: this.operationId
          },
          query: { from: 'OperationDevisFacturesIndex' }
        })
        return
      }

      if (hasDevis && hasFacture) {
        this.$bvModal.show('edit-choice-modal')
      } else if (hasFacture) {
        this.editFactures()
      } else if (hasDevis) {
        this.editDevis()
      } else {
        this.$bvToast.toast(this.$t('Aucun élément à modifier pour cette ligne'), {
          variant: 'warning',
          solid: true
        })
      }
    },

    getDocument(fileUrl) {
      let url = process.env.SERVER_URL + fileUrl
      window.open(url, '_blank').focus();
    },

    truncateText(text, maxLength) {
      if (text.length > maxLength) {
        return text.substring(0, maxLength - 3) + '...';
      }
      return text;
    },

    getStatusClass(status) {
      switch (status) {
        case 'PAYE':
        case 'PAID':
          return 'status-paye'
        case 'A_PAYER':
        case 'UNPAID':
          return 'status-a-payer'
        case 'RESTE_A_PAYER':
          return 'status-reste-a-payer'
        case 'EN_ATTENTE':
        case 'PENDING':
          return 'status-en-attente'
        default:
          return 'status-default'
      }
    },

    getStatusText(status, factures) {
      switch (status) {
        case 'PAYE':
        case 'PAID':
          return this.$t('status.paye')
        case 'A_PAYER':
        case 'UNPAID':
          return this.$t('status.aPayer')
        case 'RESTE_A_PAYER':
          return this.$t('status.resteAPayer')
        case 'EN_ATTENTE':
        case 'PENDING':
          return this.$t('status.enAttente')
        default:
          return status
      }
    },


    convertCentimesToEuros(centimes) {
      if (!centimes && centimes !== 0) return '-'

      const euros = (centimes / 100).toFixed(2)
      const formatter = new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
      })
      return formatter.format(euros)
    },

    getBudgetFromDevis(operationDevisFacture) {
      if (operationDevisFacture.devis && (operationDevisFacture.devis.montant || operationDevisFacture.devis.montant === 0)) {
        return this.convertCentimesToEuros(operationDevisFacture.devis.montant)
      }
      return '-'
    },

    clickOnCreateDevisBtn() {
      this.$bvModal.show('devis-type-selection-modal')
    },

    clickOnCreateFactureBtn() {
      this.$router.push({
        name: 'OperationFactureCreate',
        params: {
          operationId: this.operationId,
        },
        query: {
          from: 'OperationDevisFacturesIndex'
        }
      })
    },

    applyFilters() {
      this.saveFiltersToStorage()
    },

    async loadArtisans() {
      try {
        const formData = new FormData()
        formData.append('restrictbylong-operationId', this.operationId)
        formData.append('includeArtisans', 'true')
        formData.append('perPage', '1000')
        formData.append('currentPage', '1')
        formData.append('sortBy', '')
        formData.append('sortDesc', 'false')
        formData.append('filters', '[]')

        const response = await api.post(`/operationDevisFacture/getB122TableResults`, formData)

        let artisans = []
        if (Array.isArray(response.data)) {
          artisans = response.data
        } else if (response.data && Array.isArray(response.data.items)) {
          artisans = response.data.items
        } else if (response.data && Array.isArray(response.data.results)) {
          artisans = response.data.results
        }


        this.artisanFilterOptions = [
          {value: null, text: this.$t('Artisans')},
          ...artisans.map(artisan => ({
            value: artisan,
            text: artisan
          }))
        ]
      } catch (error) {
        console.error('Erreur lors du chargement des artisans:', error)
      }
    },

    async loadLots() {
      try {
        const formData = new FormData()
        formData.append('restrictbylong-operationId', this.operationId)
        formData.append('includeLots', 'true')
        formData.append('perPage', '1000')
        formData.append('currentPage', '1')
        formData.append('sortBy', '')
        formData.append('sortDesc', 'false')
        formData.append('filters', '[]')

        const response = await api.post(`/operationDevisFacture/getB122TableResults`, formData)

        let lots = []
        if (Array.isArray(response.data)) {
          lots = response.data
        } else if (response.data && Array.isArray(response.data.items)) {
          lots = response.data.items
        } else if (response.data && Array.isArray(response.data.results)) {
          lots = response.data.results
        }

        this.lotFilterOptions = [
          {value: null, text: this.$t('Lots')},
          ...lots.map(lot => ({
            value: lot,
            text: lot
          }))
        ]
      } catch (error) {
        console.error('Erreur lors du chargement des lots:', error)
      }
    },

    editDevis() {

      this.$bvModal.hide('edit-choice-modal')


      if (this.devisArray.length === 0) {

        if (this.selectedItemForEdit && this.selectedItemForEdit.sourceType) {
          if (this.selectedItemForEdit.sourceType === 'construction') {
            this.$router.push({
              name: 'OperationConstructionEdit',
              params: {
                operationId: this.operationId,
                constructionId: this.selectedItemForEdit.relatedSourceIds[0]
              }
            })
          } else if (this.selectedItemForEdit.sourceType === 'honoraire') {
            this.$router.push({
              name: 'OperationHonoraireEdit',
              params: {
                operationId: this.operationId,
                honoraireId: this.selectedItemForEdit.relatedSourceIds[0]
              }
            })
          }
        }
      } else if (this.devisArray.length === 1) {
        this.$router.push({
          name: 'OperationDevisEdit',
          params: {
            operationDevisId: this.devisArray[0].id,
            operationId: this.operationId
          },
          query: {from: 'OperationDevisFacturesIndex'}
        })
      } else {
        setTimeout(() => {
          this.$bvModal.show('devis-selection-modal')
        }, 300)
      }
    },

    editFactures() {
      this.$bvModal.hide('edit-choice-modal')

      if (this.selectedItemForEdit && this.selectedItemForEdit.facture && this.selectedItemForEdit.facture.id) {
        this.$router.push({
          name: 'OperationFactureEdit',
          params: {
            operationId: this.operationId,
            factureId: this.selectedItemForEdit.facture.id
          },
          query: {
            from: 'OperationDevisFacturesIndex'
          }
        })
      } else {
        this.$bvToast.toast(this.$t('Aucune facture à modifier pour cette ligne'), {
          variant: 'warning',
          solid: true
        })
      }
    },

    hideEditModal() {

    },

    showDevis() {
      this.$bvModal.hide('show-choice-modal')

      if (this.selectedItemForEdit && this.selectedItemForEdit.devis && this.selectedItemForEdit.devis.id) {
        this.$router.push({
          name: 'OperationDevisFactureShow',
          params: {
            operationId: this.operationId,
            devisId: this.selectedItemForEdit.devis.id
          }
        })
      }
    },

    showFacture() {
      this.$bvModal.hide('show-choice-modal')

      if (this.selectedItemForEdit && this.selectedItemForEdit.facture && this.selectedItemForEdit.facture.id) {
        this.$router.push({
          name: 'OperationFactureShow',
          params: {
            operationId: this.operationId,
            factureId: this.selectedItemForEdit.facture.id
          }
        })
      }
    },

    hideShowModal() {
      this.selectedItemForEdit = null
    },

    hideDevisModal() {
      this.selectedItemForEdit = null
    },


    editDevisItem(devis) {

      this.$bvModal.hide('devis-selection-modal')

      this.$router.push({
        name: 'OperationDevisEdit',
        params: {
          operationDevisId: devis.id,
          operationId: this.operationId
        },
        query: {
          from: 'OperationDevisFacturesIndex'
        }
      })
    },

    formatEuro(centimes) {
      if (!centimes && centimes !== 0) return '-'
      const euros = (centimes / 100).toFixed(2)
      const formatter = new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
      })
      return formatter.format(euros)
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'
      try {
        const parts = dateString.split('-')
        if (parts.length === 3) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`
        }
        return dateString
      } catch (error) {
        console.error('Erreur formatage date:', error)
        return dateString
      }
    },

    onTableRefreshed() {

    },

    clickOnDeleteBtn(row) {


      this.selectedItemForDelete = row.item.operationDevisFacture
      this.$bvModal.show('delete-confirmation-modal')
    },

    async confirmDelete() {
      try {
        let endpoint
        if (this.selectedDocumentFilter === 'factures_all' || this.selectedDocumentFilter === 'factures_only') {

          endpoint = `/operationDevisFacture/deleteFactureOnly/${this.selectedItemForDelete.id}`
        } else {

          endpoint = `/operationDevisFacture/deleteCascade/${this.selectedItemForDelete.id}`
        }

        const response = await api.delete(endpoint)

        this.$bvModal.hide('delete-confirmation-modal')

        this.$refs.B122TableDevisFactures.refreshTable()

        this.$bvToast.toast('Suppression effectuée avec succès', {
          autoHideDelay: 4000,
          toaster: 'b-toaster-bottom-right',
          noCloseButton: true,
          variant: 'success',
          solid: true,
        })

        this.selectedItemForDelete = null
      } catch (error) {
        this.$bvToast.toast('Erreur lors de la suppression', {
          autoHideDelay: 4000,
          toaster: 'b-toaster-bottom-right',
          noCloseButton: true,
          variant: 'danger',
          solid: true,
        })
      }
    },

    async deleteFactureOnly() {
      try {

        const endpoint = `/operationDevisFacture/deleteFactureOnly/${this.selectedItemForDelete.id}`

        const response = await api.delete(endpoint)


        this.$bvModal.hide('delete-confirmation-modal')
        this.$refs.B122TableDevisFactures.refreshTable()

        this.$bvToast.toast('Facture supprimée avec succès', {
          autoHideDelay: 4000,
          toaster: 'b-toaster-bottom-right',
          noCloseButton: true,
          variant: 'success',
          solid: true,
        })

        this.selectedItemForDelete = null


      } catch (error) {

        this.$bvToast.toast('Erreur lors de la suppression de la facture', {
          autoHideDelay: 4000,
          toaster: 'b-toaster-bottom-right',
          noCloseButton: true,
          variant: 'danger',
          solid: true,
        })
      }
    },

    async deleteCascade() {
      try {
        const endpoint = `/operationDevisFacture/deleteCascade/${this.selectedItemForDelete.id}`
        const response = await api.delete(endpoint)
        this.$bvModal.hide('delete-confirmation-modal')
        this.$refs.B122TableDevisFactures.refreshTable()

        this.$bvToast.toast('Suppression en cascade effectuée avec succès', {
          autoHideDelay: 4000,
          toaster: 'b-toaster-bottom-right',
          noCloseButton: true,
          variant: 'success',
          solid: true,
        })

        this.selectedItemForDelete = null


      } catch (error) {
        this.$bvToast.toast('Erreur lors de la suppression en cascade', {
          autoHideDelay: 4000,
          toaster: 'b-toaster-bottom-right',
          noCloseButton: true,
          variant: 'danger',
          solid: true,
        })
      }
    },

    cancelDelete() {
      this.$bvModal.hide('delete-confirmation-modal')
      this.selectedItemForDelete = null
    },

    hideDevisTypeModal() {

    },

    createConstructionDevis() {
      this.$bvModal.hide('devis-type-selection-modal')
      this.$router.push({
        name: 'OperationBilanConstructionEdit',
        params: {
          operationId: this.operationId,
          operationBilanId: this.operationBilanId
        }
      })
    },

    createHonoraireDevis() {
      this.$bvModal.hide('devis-type-selection-modal')
      this.$router.push({
        name: 'OperationBilanHonorairesEdit',
        params: {
          operationId: this.operationId,
          operationBilanId: this.operationBilanId
        }
      })
    }
  }
}
</script>

<style scoped>
/* Badge de statut moderne avec radius */
.status-badge {
  display: inline-block;
  padding: 6px 12px;
  font-size: 0.85rem;
  font-weight: 500;
  text-align: center;
  border-radius: 1000px;
  min-width: 120px;
  transition: all 0.2s ease;
  white-space: nowrap;
}

/* Payé - Vert clair avec texte vert foncé */
.status-paye {
  background-color: #317B221A;
  color: #317B22;
}

/* À payer - Rouge clair avec texte rouge foncé */
.status-a-payer {
  background-color: #9E04301A;
  color: #9E0430;
}

/* Reste à payer - Orange clair avec texte orange foncé */
.status-reste-a-payer {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

/* En attente - Bleu clair avec texte bleu foncé */
.status-en-attente {
  background-color: #009DFF1A;
  color: #009DFF;
}

/* Par défaut - Gris clair */
.status-default {
  background-color: #f8f9fa;
  color: #6c757d;
  border: 1px solid #dee2e6;
}

/* Effet hover pour tous les badges */
.status-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.text-muted-grey {
  font-size: 0.8rem;
  color: #AAAAAA;
}

.font-weight-medium {
  font-weight: 500;
}

/* Badge de statut petit pour les factures individuelles */
.status-badge-small {
  display: inline-block;
  padding: 3px 8px;
  font-size: 0.75rem;
  font-weight: 500;
  text-align: center;
  border-radius: 1000px;
  min-width: 60px;
  transition: all 0.2s ease;
  white-space: nowrap;
}

/* Styles pour la modale de sélection des factures */
.facture-item {
  transition: all 0.2s ease;
  border: 1px solid #dee2e6 !important;
}

.facture-item:hover {
  background-color: #f8f9fa;
  border-color: #007bff !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Styles pour la modale de sélection des devis */
.devis-item {
  transition: all 0.2s ease;
  border: 1px solid #dee2e6 !important;
}

.devis-item:hover {
  background-color: #f8f9fa;
  border-color: #007bff !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.cursor-pointer {
  cursor: pointer;
}

</style>



// WEBPACK FOOTER //
// src/views/operationDevisFactures/OperationDevisFacturesIndex.vue